import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { camelize } from 'humps';

import { DataDictionarySearch } from '../../molecules';
import { H3, Capitalize, Text } from '../../../../../components';
import { DatabaseDescriptionTable } from '../DatabaseDescriptionTable';
import { SchemasAccordionMenu } from '../SchemasAccordionMenu';
import { databaseDescriptionShape, sourceShape } from '../../../../../propsShape';

const DatabaseDescriptionContainer = styled.div`
  border-left: 1px solid var(--border-muted);
  padding: 24px;
  width: 100%;
  height: 100%;
  gap: 12px;
  overflow-y: auto;
  box-sizing: border-box;
`;

const TableListContainer = styled.div`
  width: 25%;
  display: flex;
  flex-direction: column;
  padding: 24px;
  overflow-y: auto;
  gap: 8px;
`;

const DataDictionarySourceViewContainer = styled.div`
  display: flex;
  overflow: hidden;
  border-top: 1px solid var(--border-muted);
  height: 100%;
`;

const NoResultsContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
`;

const DataDictionarySearchContainer = styled.div`
  padding: 0 24px 16px 24px;
`;

const DataDictionarySourceView = ({
  onToggleColumnCheckbox,
  selectedTable,
  databaseDescription,
  selectedItems = [],
  setSelectedTable,
  sources,
  isReadOnly = false
}) => {
  const getSourceName = useCallback(
    (name) =>
      sources?.find((source) => source?.integration.schema === name)?.integration.displayName ||
      name,
    [sources]
  );

  const getSourcePicture = useCallback(
    (name) =>
      sources?.find((source) => source?.integration.schema === name)?.integration.pictureUrl,
    [sources]
  );

  const [filteredDatabaseDescription, setFilteredDatabaseDescription] = useState(
    databaseDescription
      .slice() // Create a copy to avoid mutating the original array
      .sort((a, b) => {
        const nameA = getSourceName(a.name)?.toLowerCase() || '';
        const nameB = getSourceName(b.name)?.toLowerCase() || '';
        return nameA.localeCompare(nameB);
      })
  );

  useEffect(() => {
    if (filteredDatabaseDescription?.length > 0) {
      setSelectedTable(Object.keys(filteredDatabaseDescription[0]?.views)?.[0]);
    } else {
      setSelectedTable(null);
    }
  }, [filteredDatabaseDescription, setSelectedTable]);

  const handleSearch = (keyword) => {
    const lowerKeyword = keyword.toLowerCase();

    const searchResult = databaseDescription
      .map(({ views, ...schema }) => {
        const filteredViews = Object.fromEntries(
          Object.entries(views)
            .map(([viewName, view]) => {
              const columns = view.columns.filter(
                ({ name: colName, description }) =>
                  colName.toLowerCase().includes(lowerKeyword) ||
                  description?.toLowerCase().includes(lowerKeyword)
              );
              return columns.length ? [viewName, { ...view, columns }] : null;
            })
            .filter(Boolean)
        );
        return Object.keys(filteredViews).length ? { ...schema, views: filteredViews } : null;
      })
      .filter(Boolean);

    setFilteredDatabaseDescription(searchResult);
    if (searchResult.length) setSelectedTable(Object.keys(searchResult[0].views)[0]);
  };

  return (
    <>
      <DataDictionarySearchContainer>
        <DataDictionarySearch onChange={handleSearch} />
      </DataDictionarySearchContainer>
      <DataDictionarySourceViewContainer>
        <TableListContainer>
          {!selectedTable && (
            <NoResultsContainer>
              <H3>No matching sources found</H3>
            </NoResultsContainer>
          )}
          {selectedTable && (
            <SchemasAccordionMenu
              schemas={filteredDatabaseDescription}
              sources={sources}
              selectedTable={selectedTable}
              setSelectedTable={setSelectedTable}
              getSourceName={getSourceName}
              getSourcePicture={getSourcePicture}
            />
          )}
        </TableListContainer>
        <DatabaseDescriptionContainer>
          {!selectedTable && (
            <NoResultsContainer>
              <H3>No columns found</H3>
              <Text>Try using different keywords</Text>
            </NoResultsContainer>
          )}
          {selectedTable && (
            <>
              <H3>
                <Capitalize string={selectedTable.split('.')[1]} />
              </H3>
              <DatabaseDescriptionTable
                table={
                  filteredDatabaseDescription.filter(
                    (schema) => camelize(schema.name) === selectedTable.split('.')[0]
                  )?.[0]?.views[selectedTable] || null
                }
                tableName={selectedTable.split('.')[1]}
                onToggleColumnCheckbox={onToggleColumnCheckbox}
                selectedTable={selectedTable}
                selectedItems={selectedItems}
                isReadOnly={isReadOnly}
              />
            </>
          )}
        </DatabaseDescriptionContainer>
      </DataDictionarySourceViewContainer>
    </>
  );
};

DataDictionarySourceView.propTypes = {
  onToggleColumnCheckbox: PropTypes.func.isRequired,
  databaseDescription: databaseDescriptionShape.isRequired,
  selectedTable: PropTypes.string.isRequired,
  selectedItems: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string,
      table: PropTypes.string,
      tag: PropTypes.string
    })
  ),
  setSelectedTable: PropTypes.func.isRequired,
  sources: PropTypes.arrayOf(sourceShape).isRequired,
  isReadOnly: PropTypes.bool
};

export { DataDictionarySourceView };
